import type { FC } from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import { FullStringListModal } from "pro-features/components/Rule/src/RuleModes/EditRule/EditRuleModals/FullStringListModal";
import type {
    ICompleteRuleChipItem,
    IRuleSelectedValuesRowProps,
} from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import {
    CompleteRuleChipItem,
    CustomStringsChipsContainer,
    CustomStringsChipsRowContainer,
    CustomStringsViewAllChipsLink,
    OrLogicSeparator,
} from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/segmentRulesStyles";

const MAX_CHIPS_IN_ROW = 10;

type ChipItemComponentProps = ICompleteRuleChipItem & { exactString?: string };

const ChipItemComponent = (props: ChipItemComponentProps) => (
    <CompleteRuleChipItem {...props}>{props.exactString}</CompleteRuleChipItem>
);

export const RuleSelectedValuesRow: FC<IRuleSelectedValuesRowProps> = (props) => {
    const {
        valuesList,
        title,
        onRemoveValues,
        isFilterMode,
        onRuleEdit,
        maxChipsInRow = MAX_CHIPS_IN_ROW,
        maxChipWidth,
        ruleType,
        filterType,
        actualCurrentIndex,
    } = props;

    const TOTAL_EDIT_ICON_SIZE = 25;
    const TOTAL_PLUS_ITEMS_SIZE = 25;
    const CHIP_PADDING_SIZE = 16;
    const chipsContainerRef = useRef(null);
    const chipsViewAllLinkRef = useRef(null);
    const [isOpenViewAllChipsModal, setIsOpenViewAllChipsModal] = useState(false);

    const openViewAllChipsModal = useCallback(() => {
        setIsOpenViewAllChipsModal(true);
    }, []);

    const closeViewAllChipsModal = useCallback(() => {
        setIsOpenViewAllChipsModal(false);
    }, []);

    const allowedTextWidth = useMemo(
        () => maxChipWidth - TOTAL_EDIT_ICON_SIZE - TOTAL_PLUS_ITEMS_SIZE - CHIP_PADDING_SIZE * 2,
        [maxChipWidth],
    );

    const { concatenatedSelectedValues, hiddenItemsCount } = useMemo(() => {
        const COMMA_CHAR_CODE = 44;
        let resultString = "";
        let wordCount = 0;
        if (allowedTextWidth) {
            // Approximate calculation of how many pixels each character takes
            let availableWidth = Math.round(allowedTextWidth / 6.5);
            while (
                wordCount < valuesList.length &&
                (valuesList[wordCount].length < availableWidth || wordCount === 0)
            ) {
                resultString = resultString.concat(`${valuesList[wordCount]}, `);
                availableWidth -= valuesList[wordCount].length;
                wordCount++;
            }
            if (resultString.length > 0) {
                // last index position of space and comma suffix characters
                const lastIndexPosition = resultString.length - 2;
                if (resultString.charCodeAt(lastIndexPosition) === COMMA_CHAR_CODE) {
                    resultString = resultString.substring(0, lastIndexPosition);
                }
            }
            const hiddenItems = valuesList.length - wordCount;
            return {
                concatenatedSelectedValues: resultString,
                hiddenItemsCount: resultString && hiddenItems > 0 ? hiddenItems : undefined,
            };
        }
        return { concatenatedSelectedValues: resultString, hiddenItemsCount: undefined };
    }, [valuesList, allowedTextWidth]);

    // show view all strings button in case not all strings are viewed in a single row.
    useLayoutEffect(() => {
        if (chipsContainerRef.current && chipsViewAllLinkRef.current) {
            chipsViewAllLinkRef.current.style.display = "none";
            if (
                valuesList.length > maxChipsInRow ||
                chipsContainerRef.current.scrollHeight > chipsContainerRef.current.offsetHeight
            ) {
                chipsViewAllLinkRef.current.style.display = "block";
            }
        }
    }, [valuesList, maxChipsInRow]);

    return (
        <CustomStringsChipsRowContainer isFilterMode={isFilterMode}>
            <CustomStringsChipsContainer ref={chipsContainerRef}>
                {!isFilterMode && (
                    <span data-automation-chip-edit-icon={`${ruleType}-${actualCurrentIndex}-${filterType}`}>
                        <ChipItemComponent
                            onCloseItem={onRuleEdit}
                            text={concatenatedSelectedValues}
                            actionIconName="edit-icon"
                            maxChipWidth={allowedTextWidth}
                            plusHiddenItems={hiddenItemsCount}
                            tooltipText={valuesList.join(", ")}
                        />
                    </span>
                )}
                {isFilterMode &&
                    valuesList.slice(0, maxChipsInRow).map((exactString, idx) => {
                        return (
                            <>
                                {idx > 0 && (
                                    <OrLogicSeparator>
                                        {i18nFilter()("segmentWizard.redesigned.filter.row.or.separator")}
                                    </OrLogicSeparator>
                                )}
                                <ChipItemComponent
                                    key={exactString}
                                    text={exactString}
                                    onCloseItem={() => onRemoveValues([exactString])}
                                />
                            </>
                        );
                    })}
            </CustomStringsChipsContainer>
            {isFilterMode && (
                <CustomStringsViewAllChipsLink
                    ref={chipsViewAllLinkRef}
                    onClick={openViewAllChipsModal}
                >
                    {i18nFilter()("segmentWizard.editRule.custom.string.row.button.viewAll", {
                        count: valuesList.length,
                    })}
                </CustomStringsViewAllChipsLink>
            )}

            <FullStringListModal
                isOpen={isOpenViewAllChipsModal}
                stringsList={valuesList}
                typeName={title.toLowerCase()}
                onRemoveStrings={onRemoveValues}
                onClose={closeViewAllChipsModal}
            />
        </CustomStringsChipsRowContainer>
    );
};
